import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Package, PackageCheck, Quote } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { logUpdate } from '@/lib/audit-log';
import { resolveSampleProblem, setSampleReceiptForCreator } from '@/lib/workerApi';
import {
  SAMPLE_PROBLEM_LABEL, SAMPLE_RECEIPT_COLUMNS, invalidateSampleReceiptQueries,
  type SampleProblemType, type SampleReceipt,
} from '@/lib/sample-receipt';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';

/* ============================================================================
 * Creator-confirmed sample receipt — admin view of ONE hire
 * ============================================================================
 * The creator app asks "Did your sample arrive?" once hired: "I received it"
 * or "I'm having a problem" (+ type and details). This card shows the answer
 * and lets ops CLOSE a reported problem — e.g. the brand reshipped and the
 * creator said so in support chat but never tapped the button. Closing marks
 * the sample received, which is also what the creator's own "It arrived"
 * does, so both roads end in the same state.
 *
 * The write goes through the Brand-Dashboard worker (admin secret + this
 * admin's JWT), not a direct UPDATE: no column grant exists on these fields,
 * because creators share the `authenticated` role and would otherwise be able
 * to write them past the worker's validation.
 *
 * Fetched on its own so a missing migration can only blank this card, never
 * the hire page's main query.
 * ========================================================================== */

function fmtDate(iso: string | null | undefined) {
  return iso ? format(new Date(iso), 'MMM d, yyyy · h:mm a') : null;
}

export function SampleReceiptCard({ hireId, creatorId, campaignId, creatorName, hireStatus, settledAt }: {
  hireId: string;
  creatorId: string;
  campaignId: string;
  creatorName: string;
  /** This hire's own `status` — together with `settledAt`, decides whether
   *  the creator app is STILL capable of asking the sample question right
   *  now (see `live` below). */
  hireStatus: string;
  settledAt: string | null;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [problemType, setProblemType] = useState<SampleProblemType>('not_arrived');

  // Same gate the creator app itself uses to show the "did your sample
  // arrive?" prompt at all (app/job/[id].tsx, ActiveHires' canAnswer): only
  // while status is 'confirmed' and the hire hasn't settled. A finished hire
  // will never be asked again, so an unanswered one isn't "awaiting"
  // anything — see the two `!status` branches below.
  const live = hireStatus === 'confirmed' && !settledAt;

  const { data: receipt, isLoading } = useQuery<SampleReceipt | null>({
    queryKey: ['hire-sample-receipt', hireId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_hires')
        .select(SAMPLE_RECEIPT_COLUMNS)
        .eq('id', hireId)
        .maybeSingle();
      // Fail-soft: most likely the migration isn't applied yet.
      if (error) { console.warn('[sample-receipt] lookup failed', error); return null; }
      return (data as unknown as SampleReceipt | null) ?? null;
    },
    enabled: !!hireId,
  });

  if (!isLoading && !receipt) return null;

  const status = receipt?.sample_receipt_status ?? null;
  const hadProblem = !!receipt?.sample_problem_reported_at;

  const handleResolve = async () => {
    if (!receipt || saving) return;
    setSaving(true);
    const oldValues = {
      sample_receipt_status: receipt.sample_receipt_status,
      sample_problem_type: receipt.sample_problem_type,
      sample_problem_note: receipt.sample_problem_note,
    };
    const newValues = {
      sample_receipt_status: 'received',
      sample_problem_resolved_by: 'admin',
      sample_problem_resolution_note: note.trim() || null,
    };
    try {
      await resolveSampleProblem(hireId, note);
      // Refresh right after the write lands, before the audit log, so a slow
      // or failing log call can't leave any view on the old state.
      invalidateSampleReceiptQueries(qc, hireId);
      await logUpdate('cr_hires', hireId, oldValues, newValues, {
        description: `Admin resolved creator sample problem (${SAMPLE_PROBLEM_LABEL[receipt.sample_problem_type ?? 'other']}) — marked received`,
        creator_id: creatorId,
        campaign_id: campaignId,
        status: 'success',
      });
      toast.success('Sample problem resolved', { description: `${creatorName}'s sample is now marked received.` });
      setNote('');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to resolve';
      await logUpdate('cr_hires', hireId, oldValues, newValues, {
        description: 'Failed to resolve creator sample problem',
        creator_id: creatorId,
        campaign_id: campaignId,
        status: 'failed',
        error: msg,
      });
      toast.error('Could not resolve', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  // Answer for a creator who hasn't (told support instead of using the app).
  const handleAnswerForCreator = async (answer: 'received' | 'problem') => {
    if (saving) return;
    if (answer === 'problem' && problemType === 'other' && note.trim().length < 10) {
      toast.error('Describe the problem', { description: 'Add at least 10 characters for "Other issue".' });
      return;
    }
    setSaving(true);
    const oldValues = { sample_receipt_status: null };
    const newValues = answer === 'received'
      ? { sample_receipt_status: 'received', sample_receipt_source: 'admin' }
      : { sample_receipt_status: 'problem', sample_receipt_source: 'admin', sample_problem_type: problemType, sample_problem_note: note.trim() || null };
    const label = answer === 'received' ? 'marked sample received' : `recorded sample problem (${SAMPLE_PROBLEM_LABEL[problemType]})`;
    try {
      await setSampleReceiptForCreator(
        hireId,
        answer === 'received' ? { status: 'received' } : { status: 'problem', problem_type: problemType },
        note,
      );
      invalidateSampleReceiptQueries(qc, hireId);
      await logUpdate('cr_hires', hireId, oldValues, newValues, {
        description: `Admin ${label} on the creator's behalf`,
        creator_id: creatorId,
        campaign_id: campaignId,
        status: 'success',
      });
      toast.success(answer === 'received' ? 'Marked received' : 'Problem recorded', { description: `Saved for ${creatorName}.` });
      setNote('');
      setReporting(false);
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save';
      await logUpdate('cr_hires', hireId, oldValues, newValues, {
        description: `Failed to ${label} on the creator's behalf`,
        creator_id: creatorId,
        campaign_id: campaignId,
        status: 'failed',
        error: msg,
      });
      toast.error('Could not save', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={`p-5 space-y-4 ${status === 'problem' ? 'ring-1 ring-amber-300/70 bg-amber-50/40 dark:bg-amber-500/5' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Package className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-semibold text-sm">Sample Receipt</h2>
        {isLoading ? null : status === 'received' ? (
          <Badge className="bg-green-100 text-green-800 border border-green-200 gap-1">
            <PackageCheck className="h-3 w-3" /> Received
          </Badge>
        ) : status === 'problem' ? (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-200 gap-1">
            <AlertTriangle className="h-3 w-3" /> Problem reported
          </Badge>
        ) : live ? (
          <Badge variant="outline" className="text-muted-foreground gap-1">
            <Clock className="h-3 w-3" /> Awaiting creator
          </Badge>
        ) : (
          // Finished (or never went active) without an answer — nothing is
          // still pending here, so this reads as neutral, not a stalled task.
          <Badge variant="outline" className="text-muted-foreground gap-1 font-normal">
            No check-in
          </Badge>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {receipt?.sample_problem_reported_by === 'admin' || (!hadProblem && receipt?.sample_receipt_source === 'admin')
            ? 'Recorded by BMC support on the creator\'s behalf'
            : receipt?.sample_receipt_source === 'auto_video_submission'
              ? 'Auto-detected from a video submission'
              : status ? 'Reported by the creator in the app' : 'Awaiting the creator\'s answer'} · visible to the brand
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !status ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {live
              ? `${creatorName} hasn't said whether their sample arrived yet. The app asks on the job page and in Active Hires.`
              : "No sample check-in on record for this hire. The creator app only asks while a hire is active, and this one is no longer live — nothing more will come in."}
          </p>
          {['confirmed', 'completed', 'partial_completed'].includes(hireStatus) && (
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs font-medium text-muted-foreground">
                Creator told support instead of using the app? Record their answer for them.
              </p>
              {reporting && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(Object.keys(SAMPLE_PROBLEM_LABEL) as SampleProblemType[]).map(t => (
                      <Button key={t} type="button" size="sm" variant={problemType === t ? 'default' : 'outline'} onClick={() => setProblemType(t)}>
                        {SAMPLE_PROBLEM_LABEL[t]}
                      </Button>
                    ))}
                  </div>
                  <Textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    maxLength={500}
                    placeholder="What the creator told support (the brand sees this)"
                    className="min-h-[70px]"
                  />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {reporting ? (
                  <>
                    <Button onClick={() => handleAnswerForCreator('problem')} disabled={saving} className="gap-1.5">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                      Record problem
                    </Button>
                    <Button variant="ghost" onClick={() => setReporting(false)} disabled={saving}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => handleAnswerForCreator('received')} disabled={saving} className="gap-1.5">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
                      Mark sample received
                    </Button>
                    <Button variant="outline" onClick={() => setReporting(true)} disabled={saving} className="gap-1.5">
                      <AlertTriangle className="h-4 w-4" /> Report a problem
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {status === 'received' && (
            <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50/60 dark:bg-green-500/5 px-4 py-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div className="text-sm">
                <div className="font-medium">Sample received</div>
                {(fmtDate(receipt!.sample_received_at) || receipt!.sample_receipt_source === 'auto_video_submission') && (
                  <div className="text-xs text-muted-foreground">
                    {fmtDate(receipt!.sample_received_at) ? `Confirmed ${fmtDate(receipt!.sample_received_at)}` : 'Confirmed'}
                    {receipt!.sample_receipt_source === 'auto_video_submission'
                      ? ' — auto-detected from a video submission, before the creator answered'
                      : receipt!.sample_receipt_source === 'admin' && !hadProblem
                        ? ' — marked by BMC on the creator\'s behalf'
                        : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {hadProblem && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="font-semibold">{SAMPLE_PROBLEM_LABEL[receipt!.sample_problem_type ?? 'other']}</span>
                <span className="text-xs text-muted-foreground">
                  Reported {fmtDate(receipt!.sample_problem_reported_at)}
                  {receipt!.sample_problem_reported_by === 'admin' ? ' · by BMC support on the creator\'s behalf' : ' · by the creator'}
                </span>
              </div>
              {receipt!.sample_problem_note ? (
                <div className="relative rounded-lg border-l-4 border-amber-400 bg-amber-50/70 dark:bg-amber-500/10 px-4 py-3">
                  <Quote className="absolute -top-2 left-3 h-4 w-4 fill-current text-muted-foreground/30" />
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{receipt!.sample_problem_note}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No details given.</p>
              )}
              {status === 'received' && receipt!.sample_problem_resolved_at && (
                <p className="text-xs text-muted-foreground">
                  Resolved {fmtDate(receipt!.sample_problem_resolved_at)} —{' '}
                  {receipt!.sample_problem_resolved_by === 'admin' ? 'closed by BMC'
                    : receipt!.sample_problem_resolved_by === 'auto_video_submission' ? 'auto-resolved when the creator submitted a video'
                    : 'creator confirmed it arrived'}
                  {receipt!.sample_problem_resolution_note ? ` · “${receipt!.sample_problem_resolution_note}”` : ''}
                </p>
              )}
            </div>
          )}

          {status === 'problem' && (
            <div className="space-y-2 border-t pt-4">
              <label className="text-xs font-medium text-muted-foreground" htmlFor={`sample-resolve-${hireId}`}>
                Resolution note (optional — the brand sees it)
              </label>
              <Textarea
                id={`sample-resolve-${hireId}`}
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={500}
                placeholder="e.g. Brand reshipped, creator confirmed delivery over chat"
                className="min-h-[70px]"
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={handleResolve} disabled={saving} className="gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}
                  Mark resolved — sample received
                </Button>
                <span className="text-xs text-muted-foreground">
                  Only once the creator actually has the product. The creator can also close it themselves from the app.
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
